import {
  ApiError,
  type KioskDevice,
  type LocationType,
  queryKeys,
  type VendingMachine,
} from '@mevaser/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { KeyRound, Monitor, Plus, ShieldOff } from 'lucide-react'
import { useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

interface LocationOption {
  id: string
  name: string
  type: LocationType
}

const STATUS_TONE = { active: 'success', unpaired: 'warning', disabled: 'neutral' } as const

export function KiosksPage() {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canManage = can('kiosks:manage')
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [locationId, setLocationId] = useState('')
  const [pairing, setPairing] = useState<{ code: string; expiresAt: string; name: string } | null>(
    null,
  )
  const [revoking, setRevoking] = useState<KioskDevice | null>(null)

  const { data: devices, isLoading } = useQuery({
    queryKey: queryKeys.kiosks.list(),
    queryFn: () => api.get<KioskDevice[]>('/kiosks'),
    refetchInterval: 15_000,
  })

  // GET /kiosks also returns every vending machine's tablet and Pi, with no
  // marker telling them apart, so hide the ones a machine owns.
  const { data: machines } = useQuery({
    queryKey: queryKeys.vending.machines(),
    queryFn: () => api.get<VendingMachine[]>('/vending/machines'),
  })
  const { data: locations } = useQuery({
    queryKey: queryKeys.locations(),
    queryFn: () => api.get<LocationOption[]>('/locations'),
    enabled: creating,
  })
  const kioskLocations = locations?.filter((l) => l.type === 'kiosk') ?? []

  const owned = new Set<string>()
  for (const m of machines ?? []) {
    if (m.tabletDeviceId) owned.add(m.tabletDeviceId)
    if (m.piDeviceId) owned.add(m.piDeviceId)
  }
  const storeKiosks = machines ? (devices ?? []).filter((d) => !owned.has(d.id)) : []

  const create = useMutation({
    mutationFn: () => api.post('/kiosks', { name: name.trim(), locationId, note: null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kiosks.all() })
      toast.success('Kiosk added', 'Now issue a pairing code and type it on the kiosk.')
      setCreating(false)
      setName('')
      setLocationId('')
    },
    onError: (error) =>
      toast.error('Could not add the kiosk', error instanceof ApiError ? error.message : undefined),
  })

  const issueCode = useMutation({
    mutationFn: (device: KioskDevice) =>
      api.post<{ pairingCode: string; expiresAt: string }>(`/kiosks/${device.id}/pairing-code`, {}),
    onSuccess: (result, device) => {
      setPairing({ code: result.pairingCode, expiresAt: result.expiresAt, name: device.name })
      queryClient.invalidateQueries({ queryKey: queryKeys.kiosks.all() })
    },
    onError: (error) =>
      toast.error(
        'Could not issue a pairing code',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const revoke = useMutation({
    mutationFn: (id: string) => api.post(`/kiosks/${id}/revoke`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kiosks.all() })
      toast.success('Kiosk revoked')
      setRevoking(null)
    },
    onError: (error) =>
      toast.error(
        'Could not revoke the kiosk',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Page>
      <PageHeader
        title="Store kiosks"
        description="In-store browser kiosks for sign-in, checkout and plans. Vending machines pair their own tablet and Pi from the machine page."
        actions={
          canManage ? (
            <Button onClick={() => setCreating(true)}>
              <Plus /> Add kiosk
            </Button>
          ) : null
        }
      />

      {isLoading || !machines ? (
        <Card className="h-32" />
      ) : storeKiosks.length === 0 ? (
        <Card>
          <EmptyState
            icon={Monitor}
            title="No store kiosks yet"
            description="Add one, then pair it with a one-time code."
          />
        </Card>
      ) : (
        <Card className="divide-y divide-border">
          {storeKiosks.map((d) => (
            <div key={d.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div className="min-w-0">
                <p className="flex items-center gap-2 font-medium">
                  <span
                    className={`size-2 rounded-full ${d.online ? 'bg-success' : 'bg-neutral-300'}`}
                    role="img"
                    aria-label={d.online ? 'Online' : 'Offline'}
                  />
                  <span className="truncate">{d.name}</span>
                  <Badge tone={STATUS_TONE[d.status]}>{d.status}</Badge>
                </p>
                <p className="text-xs text-foreground-muted">
                  {d.locationName ?? 'No location'} · Last seen{' '}
                  {d.lastSeenAt ? new Date(d.lastSeenAt).toLocaleString() : 'never'} · Version{' '}
                  {d.agentVersion ?? '—'}
                </p>
                {d.lastError ? <p className="text-xs text-danger">{d.lastError}</p> : null}
              </div>
              {canManage ? (
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={issueCode.isPending && issueCode.variables?.id === d.id}
                    onClick={() => issueCode.mutate(d)}
                  >
                    <KeyRound /> Pairing code
                  </Button>
                  {d.status !== 'disabled' ? (
                    <Button variant="ghost" size="sm" onClick={() => setRevoking(d)}>
                      <ShieldOff /> Revoke
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>
          ))}
        </Card>
      )}

      <Dialog open={creating} onOpenChange={(open) => !open && setCreating(false)}>
        <DialogContent className="max-w-md">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (name.trim() && locationId) create.mutate()
            }}
          >
            <DialogHeader>
              <DialogTitle>Add a store kiosk</DialogTitle>
            </DialogHeader>
            <DialogBody className="space-y-4">
              <Field label="Name" required>
                {(props) => (
                  <Input
                    {...props}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Front counter kiosk"
                  />
                )}
              </Field>
              <Field label="Location" required>
                {(props) => (
                  <Select value={locationId} onValueChange={setLocationId}>
                    <SelectTrigger {...props}>
                      <SelectValue placeholder="Choose a kiosk-type location…" />
                    </SelectTrigger>
                    <SelectContent>
                      {kioskLocations.map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              {locations && kioskLocations.length === 0 ? (
                <p className="text-xs text-foreground-muted">
                  No kiosk-type locations exist yet — create one from the Locations page first.
                </p>
              ) : null}
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setCreating(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={create.isPending}
                disabled={!name.trim() || !locationId}
              >
                Add kiosk
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={pairing !== null} onOpenChange={(open) => !open && setPairing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Pairing code for {pairing?.name}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <p className="rounded-lg bg-surface-sunken py-4 text-center font-identifier text-3xl font-semibold tracking-widest">
              {pairing?.code}
            </p>
            <Alert tone="info">
              On the kiosk, open the pairing screen and type this code. It works once and expires in
              10 minutes
              {pairing ? ` (at ${new Date(pairing.expiresAt).toLocaleTimeString()})` : ''}. It is
              shown only now.
            </Alert>
          </DialogBody>
          <DialogFooter>
            <Button variant="primary" onClick={() => setPairing(null)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={revoking !== null} onOpenChange={(open) => !open && setRevoking(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Revoke {revoking?.name}?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground-muted">
              The kiosk is signed out and disabled immediately. Issue a new pairing code to restore
              it.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setRevoking(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={revoke.isPending}
              onClick={() => revoking && revoke.mutate(revoking.id)}
            >
              Revoke
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  )
}
